import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  MapPin, Plus, Edit2, Trash2, CheckCircle2, XCircle, AlertCircle,
  Clock, Shield, Car, Bike, Bus, HelpCircle, History, FileText, Check, X, Search, RefreshCw,
  Navigation
} from 'lucide-react';
import { toast } from 'sonner';
import MainLayout from '../components/MainLayout';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const DEFAULT_RATES = {
  own_bike: { key: 'own_bike', label: 'Own Bike / Two-Wheeler', calculation_type: 'per_km', rate_per_km: 5.0, max_daily_km: 50.0, max_daily_amount: 250.0, allow_fixed_fare: false, requires_bill: false },
  own_car: { key: 'own_car', label: 'Own Car / Four-Wheeler', calculation_type: 'per_km', rate_per_km: 10.0, max_daily_km: 50.0, max_daily_amount: 500.0, allow_fixed_fare: false, requires_bill: false },
  public_transport: { key: 'public_transport', label: 'Public Transport', calculation_type: 'actual_bill', rate_per_km: 0.0, max_daily_km: 0.0, max_daily_amount: 300.0, allow_fixed_fare: true, requires_bill: true },
  auto_cab: { key: 'auto_cab', label: 'Auto / Cab', calculation_type: 'actual_bill', rate_per_km: 0.0, max_daily_km: 0.0, max_daily_amount: 600.0, allow_fixed_fare: true, requires_bill: true },
  other_mode: { key: 'other_mode', label: 'Other Approved Mode', calculation_type: 'actual_bill', rate_per_km: 0.0, max_daily_km: 0.0, max_daily_amount: 200.0, allow_fixed_fare: true, requires_bill: true },
};

const LocalConveyancePolicyMaster = () => {
  const [policies, setPolicies] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [historyModalPolicy, setHistoryModalPolicy] = useState(null);

  // Form State
  const todayStr = new Date().toISOString().split('T')[0];
  const [formData, setFormData] = useState({
    policy_name: '',
    company_id: 'all',
    branch_id: '',
    status: 'ACTIVE',
    effective_from: todayStr,
    policy_application_date: todayStr,
    effective_to: '',
    max_backdated_days: 2,
    max_daily_limit: 0,
    max_monthly_limit: 0,
    requires_bill: false,
    remarks: '',
    rates: DEFAULT_RATES,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [polRes, compRes, brRes] = await Promise.all([
        axios.get(`${API_URL}/local-conveyance-policies`, { withCredentials: true }),
        axios.get(`${API_URL}/companies`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/organization/units`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);

      setPolicies(polRes.data?.policies || []);
      
      const compList = Array.isArray(compRes.data) ? compRes.data : compRes.data?.companies || [];
      setCompanies(compList);

      const brList = Array.isArray(brRes.data) ? brRes.data : brRes.data?.units || [];
      setBranches(brList);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch policy configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenCreateModal = () => {
    const curDate = new Date().toISOString().split('T')[0];
    setEditingPolicy(null);
    setFormData({
      policy_name: '',
      company_id: 'all',
      branch_id: '',
      status: 'ACTIVE',
      effective_from: curDate,
      policy_application_date: curDate,
      effective_to: '',
      max_backdated_days: 2,
      max_daily_limit: 0,
      max_monthly_limit: 0,
      requires_bill: false,
      remarks: '',
      rates: DEFAULT_RATES,
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (p) => {
    const effFrom = p.effective_from || new Date().toISOString().split('T')[0];
    setEditingPolicy(p);
    setFormData({
      policy_name: p.policy_name || '',
      company_id: p.company_id || 'all',
      branch_id: p.branch_id || '',
      status: p.status || 'ACTIVE',
      effective_from: effFrom,
      policy_application_date: p.policy_application_date || effFrom,
      effective_to: p.effective_to || '',
      max_backdated_days: p.max_backdated_days !== undefined ? p.max_backdated_days : 2,
      max_daily_limit: p.max_daily_limit || 0,
      max_monthly_limit: p.max_monthly_limit || 0,
      requires_bill: !!p.requires_bill,
      remarks: p.remarks || '',
      rates: p.rates || DEFAULT_RATES,
    });
    setIsModalOpen(true);
  };

  const handleRateChange = (vehicleKey, field, value) => {
    setFormData((prev) => ({
      ...prev,
      rates: {
        ...prev.rates,
        [vehicleKey]: {
          ...prev.rates[vehicleKey],
          [field]: (field === 'allow_fixed_fare' || field === 'requires_bill') ? Boolean(value) : parseFloat(value) || 0,
        },
      },
    }));
  };

  const handleSubmitPolicy = async (e) => {
    e.preventDefault();
    if (!formData.policy_name.trim()) {
      toast.error('Policy Name is required');
      return;
    }

    if (formData.effective_to) {
      if (formData.effective_to < formData.policy_application_date) {
        toast.error(`Effective To date (${formData.effective_to}) cannot be before Policy Application Date (${formData.policy_application_date})`);
        return;
      }
      if (formData.effective_to < formData.effective_from) {
        toast.error(`Effective To date (${formData.effective_to}) cannot be before Effective From date (${formData.effective_from})`);
        return;
      }
    }

    const selComp = companies.find((c) => String(c._id || c.id) === String(formData.company_id));
    const company_name = formData.company_id === 'all'
      ? 'All Companies'
      : (selComp?.name || selComp?.company_name || formData.company_id);

    const payload = {
      ...formData,
      company_name,
    };

    try {
      if (editingPolicy) {
        await axios.put(`${API_URL}/local-conveyance-policies/${editingPolicy.id || editingPolicy._id}`, payload, { withCredentials: true });
        toast.success('Local Conveyance Policy updated successfully');
      } else {
        await axios.post(`${API_URL}/local-conveyance-policies`, payload, { withCredentials: true });
        toast.success('Local Conveyance Policy created successfully');
      }
      setIsModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save policy');
    }
  };

  const handleToggleStatus = async (policyId, currentStatus) => {
    try {
      const nextStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      await axios.patch(`${API_URL}/local-conveyance-policies/${policyId}/status`, { status: nextStatus }, { withCredentials: true });
      toast.success(`Policy status updated to ${nextStatus}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change policy status');
    }
  };

  const handleDeletePolicy = async (policyId) => {
    if (!window.confirm('Are you sure you want to delete this Local Conveyance Policy?')) return;
    try {
      await axios.delete(`${API_URL}/local-conveyance-policies/${policyId}`, { withCredentials: true });
      toast.success('Policy deleted successfully');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete policy');
    }
  };

  // Filtered Policies
  const filteredPolicies = policies.filter((p) => {
    if (selectedCompany !== 'all' && p.company_id !== selectedCompany && p.company_id !== 'all') return false;
    if (selectedStatus !== 'all' && p.status !== selectedStatus) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchName = (p.policy_name || '').toLowerCase().includes(q);
      const matchRemarks = (p.remarks || '').toLowerCase().includes(q);
      if (!matchName && !matchRemarks) return false;
    }
    return true;
  });

  return (
    <MainLayout>
      <div className="p-6 bg-slate-50 min-h-screen">
        {/* Header Banner */}
        <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-500/20 text-blue-400 rounded-xl border border-blue-500/30 shrink-0">
              <Navigation className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-black tracking-tight">Local Conveyance Policy Master</h1>
              <p className="text-xs text-blue-200 mt-0.5">
                Manage company &amp; branch-level conveyance reimbursement rules, vehicle-wise KM rates, and daily caps.
              </p>
            </div>
          </div>
          <button
            onClick={handleOpenCreateModal}
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-extrabold px-4 py-2.5 rounded-xl shadow-md transition flex items-center space-x-1.5 shrink-0"
          >
            <Plus className="w-4 h-4" />
            <span>Create New Policy</span>
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Policies</div>
            <div className="text-2xl font-bold text-slate-800 mt-1">{policies.length}</div>
            <div className="text-xs text-slate-400 mt-1">Across all companies</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Active Policies</div>
            <div className="text-2xl font-bold text-emerald-600 mt-1">
              {policies.filter((p) => p.status === 'ACTIVE').length}
            </div>
            <div className="text-xs text-emerald-600 mt-1">Currently enforced</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Own Bike Rate</div>
            <div className="text-2xl font-bold text-blue-600 mt-1">₹5.00 / KM</div>
            <div className="text-xs text-slate-400 mt-1">Default Configurable</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Own Car Rate</div>
            <div className="text-2xl font-bold text-purple-600 mt-1">₹10.00 / KM</div>
            <div className="text-xs text-slate-400 mt-1">Default Configurable</div>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-3 flex-wrap">
            <div className="relative min-w-[220px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
              <input
                type="text"
                placeholder="Search policy name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm w-full focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
            >
              <option value="all">All Companies</option>
              {companies.map((c) => {
                const cId = c._id || c.id;
                const cName = c.name || c.company_name || c.title || c.code || cId;
                return (
                  <option key={cId} value={cId}>
                    {cName}
                  </option>
                );
              })}
            </select>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
            >
              <option value="all">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>

          <button
            onClick={fetchData}
            className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-600 flex items-center space-x-1 text-sm bg-white"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading policies...</div>
          ) : filteredPolicies.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <div className="text-base font-medium">No Local Conveyance Policies Found</div>
              <div className="text-sm text-slate-400 mt-1">
                Create a policy to configure vehicle KM rates and daily limits.
              </div>
            </div>
          ) : (
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 font-semibold text-xs uppercase tracking-wider">
                  <th className="py-3 px-4">Policy Name &amp; Version</th>
                  <th className="py-3 px-4">Scope (Company / Branch)</th>
                  <th className="py-3 px-4">Effective Dates</th>
                  <th className="py-3 px-4">Vehicle Rates (₹ / KM)</th>
                  <th className="py-3 px-4">Daily Caps</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredPolicies.map((p) => {
                  const compObj = companies.find((c) => String(c._id || c.id) === String(p.company_id));
                  const compName = p.company_id === 'all' ? 'All Companies' : (p.company_name || compObj?.name || compObj?.company_name || p.company_id || 'Specific Company');
                  const rates = p.rates || DEFAULT_RATES;

                  return (
                    <tr key={p.id || p._id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="py-3.5 px-4">
                        <div className="font-semibold text-slate-800">{p.policy_name}</div>
                        <div className="flex items-center space-x-2 mt-0.5">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-700">
                            v{p.version || 1}
                          </span>
                          {p.requires_bill && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700">
                              Bill Required
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="text-slate-700 font-medium">{compName}</div>
                        <div className="text-xs text-slate-400">{p.branch_id ? `Branch ID: ${p.branch_id}` : 'All Branches'}</div>
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="text-slate-700 text-xs font-semibold flex items-center gap-1">
                          <span>Apply From:</span>
                          <span className="font-bold text-slate-900">{p.policy_application_date || p.effective_from}</span>
                        </div>
                        <div className="mt-0.5">
                          {p.application_type === 'RETROACTIVE' || (p.policy_application_date && p.policy_application_date < p.effective_from) ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-extrabold bg-amber-100 text-amber-800 border border-amber-300">
                              Retroactive
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-extrabold bg-blue-50 text-blue-700 border border-blue-200">
                              Prospective
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-1">
                          Effective: {p.effective_from} → {p.effective_to ? p.effective_to : 'Indefinite'}
                        </div>
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="flex flex-wrap gap-1">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                            <Bike className="w-3 h-3 mr-1 text-slate-500" /> ₹{rates.own_bike?.rate_per_km}/km
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700">
                            <Car className="w-3 h-3 mr-1 text-purple-500" /> ₹{rates.own_car?.rate_per_km}/km
                          </span>
                        </div>
                      </td>
                      <td className="py-3.5 px-4 text-xs">
                        <div>Daily Max: <span className="font-semibold text-slate-700">{p.max_daily_limit > 0 ? `₹${p.max_daily_limit}` : 'No Cap'}</span></div>
                        <div className="text-slate-400">Monthly: {p.max_monthly_limit > 0 ? `₹${p.max_monthly_limit}` : 'No Cap'}</div>
                        <div className="text-indigo-700 font-bold mt-0.5">
                          Backdated Limit: {p.max_backdated_days !== undefined ? p.max_backdated_days : 2} day(s)
                        </div>
                      </td>
                      <td className="py-3.5 px-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            p.status === 'ACTIVE'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-slate-100 text-slate-600 border border-slate-200'
                          }`}
                        >
                          {p.status === 'ACTIVE' ? <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-500" /> : <XCircle className="w-3 h-3 mr-1 text-slate-400" />}
                          {p.status}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => setHistoryModalPolicy(p)}
                            title="View Version & Audit History"
                            className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded"
                          >
                            <History className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(p.id || p._id, p.status)}
                            title={p.status === 'ACTIVE' ? 'Deactivate Policy' : 'Activate Policy'}
                            className={`p-1.5 rounded ${p.status === 'ACTIVE' ? 'text-amber-600 hover:bg-amber-50' : 'text-emerald-600 hover:bg-emerald-50'}`}
                          >
                            {p.status === 'ACTIVE' ? <XCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => handleOpenEditModal(p)}
                            title="Edit Policy & Increment Version"
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeletePolicy(p.id || p._id)}
                            title="Delete Policy"
                            className="p-1.5 text-rose-600 hover:bg-rose-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* CREATE / EDIT MODAL */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl overflow-hidden my-8">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                <div>
                  <h3 className="text-lg font-bold text-slate-800">
                    {editingPolicy ? `Edit Policy (Current Version v${editingPolicy.version || 1})` : 'Create New Local Conveyance Policy'}
                  </h3>
                  <p className="text-xs text-slate-500">
                    {editingPolicy ? 'Saving changes will auto-increment policy version to v' + ((editingPolicy.version || 1) + 1) : 'Configure vehicle rates, daily limits, and scope.'}
                  </p>
                </div>
                <button onClick={() => setIsModalOpen(false)} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmitPolicy} className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
                {/* Basic Fields */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Policy Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.policy_name}
                      onChange={(e) => setFormData({ ...formData, policy_name: e.target.value })}
                      placeholder="e.g., Standard Corporate Conveyance Policy"
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Company Scope</label>
                    <select
                      value={formData.company_id}
                      onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="all">All Companies</option>
                      {companies.map((c) => {
                        const cId = c._id || c.id;
                        const cName = c.name || c.company_name || c.title || c.code || cId;
                        return (
                          <option key={cId} value={cId}>
                            {cName}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="INACTIVE">INACTIVE</option>
                    </select>
                  </div>
                </div>

                {/* Effective Dates & Application Settings */}
                <div className="space-y-4 bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div className="md:col-span-2 bg-white p-3 rounded-lg border border-blue-200 shadow-sm">
                      <label className="block text-xs font-bold text-blue-900 uppercase mb-1">
                        Policy Application Date (Apply Policy From) *
                      </label>
                      <input
                        type="date"
                        required
                        value={formData.policy_application_date}
                        onChange={(e) => setFormData({ ...formData, policy_application_date: e.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white font-semibold focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      />
                      <div className="mt-1.5 flex items-center justify-between text-[11px]">
                        <span className="text-slate-500">Application Mode:</span>
                        {formData.policy_application_date < formData.effective_from ? (
                          <span className="font-extrabold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                            Retroactive (Past Date)
                          </span>
                        ) : (
                          <span className="font-extrabold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">
                            Prospective (Future / Current)
                          </span>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Effective From *</label>
                      <input
                        type="date"
                        required
                        value={formData.effective_from}
                        onChange={(e) => setFormData({ ...formData, effective_from: e.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Effective To (Optional)</label>
                      <input
                        type="date"
                        value={formData.effective_to || ''}
                        onChange={(e) => setFormData({ ...formData, effective_to: e.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Max Daily Limit (₹)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.max_daily_limit}
                        onChange={(e) => setFormData({ ...formData, max_daily_limit: parseFloat(e.target.value) || 0 })}
                        placeholder="0 for Unlimited"
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                      />
                    </div>
                  </div>

                  {/* Maximum Backdated Application Days */}
                  <div className="bg-white p-3.5 rounded-xl border border-indigo-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="flex-1">
                      <label className="block text-xs font-bold text-indigo-950 uppercase tracking-wide">
                        Maximum Backdated Application Days *
                      </label>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Employees can apply for today and the configured number of previous days only.
                      </p>
                    </div>
                    <div className="w-full md:w-48 shrink-0">
                      <select
                        value={formData.max_backdated_days}
                        onChange={(e) => setFormData({ ...formData, max_backdated_days: parseInt(e.target.value) || 0 })}
                        className="w-full border border-indigo-300 rounded-lg px-3 py-2 text-xs font-bold text-indigo-900 bg-indigo-50/50 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      >
                        <option value={0}>0 days (Today only)</option>
                        <option value={1}>1 day (Today + yesterday)</option>
                        <option value={2}>2 days (Today + previous 2 days)</option>
                        <option value={3}>3 days (Today + previous 3 days)</option>
                        <option value={5}>5 days (Today + previous 5 days)</option>
                        <option value={7}>7 days (Today + previous 7 days)</option>
                        <option value={15}>15 days (Today + previous 15 days)</option>
                        <option value={30}>30 days (Today + previous 30 days)</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Vehicle-Wise Reimbursement Rates Matrix */}
                <div>
                  <h4 className="text-sm font-bold text-slate-800 mb-2 flex items-center">
                    <Car className="w-4 h-4 mr-2 text-blue-600" /> Vehicle Type Reimbursement Rates Configuration
                  </h4>
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200">
                        <tr>
                          <th className="py-2.5 px-3">Vehicle / Travel Mode</th>
                          <th className="py-2.5 px-3">Basis</th>
                          <th className="py-2.5 px-3">Rate Per KM (₹)</th>
                          <th className="py-2.5 px-3">Max Daily KM Cap</th>
                          <th className="py-2.5 px-3">Max Daily Amount Cap (₹)</th>
                          <th className="py-2.5 px-3 text-center">Allow Fixed Fare?</th>
                          <th className="py-2.5 px-3 text-center">Mandatory Bill / Receipt</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {Object.keys(DEFAULT_RATES).map((vKey) => {
                          const defaultRef = DEFAULT_RATES[vKey];
                          const r = formData.rates[vKey] || defaultRef;
                          const isPerKm = (r.calculation_type || defaultRef.calculation_type) === 'per_km';

                          return (
                            <tr key={vKey} className="hover:bg-slate-50">
                              <td className="py-2 px-3 font-semibold text-slate-800">{r.label}</td>
                              <td className="py-2 px-3">
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${isPerKm ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                                  {isPerKm ? 'Per-KM' : 'Actual Bill'}
                                </span>
                              </td>
                              <td className="py-2 px-3">
                                {isPerKm ? (
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    value={r.rate_per_km}
                                    onChange={(e) => handleRateChange(vKey, 'rate_per_km', e.target.value)}
                                    className="w-24 border border-slate-300 rounded px-2 py-1 text-xs"
                                  />
                                ) : (
                                  <span className="text-slate-400 italic text-[11px]">N/A (Bill-Based)</span>
                                )}
                              </td>
                              <td className="py-2 px-3">
                                {isPerKm ? (
                                  <input
                                    type="number"
                                    min="0"
                                    value={r.max_daily_km}
                                    onChange={(e) => handleRateChange(vKey, 'max_daily_km', e.target.value)}
                                    className="w-24 border border-slate-300 rounded px-2 py-1 text-xs"
                                  />
                                ) : (
                                  <span className="text-slate-400 text-[11px]">N/A</span>
                                )}
                              </td>
                              <td className="py-2 px-3">
                                <input
                                  type="number"
                                  min="0"
                                  value={r.max_daily_amount}
                                  onChange={(e) => handleRateChange(vKey, 'max_daily_amount', e.target.value)}
                                  className="w-28 border border-slate-300 rounded px-2 py-1 text-xs"
                                />
                              </td>
                              <td className="py-2 px-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={r.allow_fixed_fare}
                                  onChange={(e) => handleRateChange(vKey, 'allow_fixed_fare', e.target.checked)}
                                  className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                                />
                              </td>
                              <td className="py-2 px-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={r.requires_bill}
                                  onChange={(e) => handleRateChange(vKey, 'requires_bill', e.target.checked)}
                                  className="w-4 h-4 text-amber-600 rounded border-slate-300 focus:ring-amber-500"
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Policy Remarks &amp; Notes</label>
                  <textarea
                    rows={2}
                    value={formData.remarks}
                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                    placeholder="Additional guidelines for employees..."
                    className="w-full border border-slate-300 rounded-lg p-2.5 text-sm"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-700 hover:bg-slate-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm rounded-lg shadow-sm"
                  >
                    {editingPolicy ? 'Update & Create Version' : 'Save Policy'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* AUDIT HISTORY MODAL */}
        {historyModalPolicy && (
          <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                <div>
                  <h3 className="text-base font-bold text-slate-800">
                    Audit History — {historyModalPolicy.policy_name}
                  </h3>
                  <p className="text-xs text-slate-500">
                    Version History &amp; Modification Log
                  </p>
                </div>
                <button onClick={() => setHistoryModalPolicy(null)} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 max-h-[60vh] overflow-y-auto space-y-4">
                {(historyModalPolicy.history || []).length === 0 ? (
                  <div className="text-center py-6 text-slate-400 text-sm">No history entries recorded yet.</div>
                ) : (
                  (historyModalPolicy.history || []).map((h, idx) => (
                    <div key={idx} className="flex space-x-3 text-xs bg-slate-50 p-3 rounded-lg border border-slate-200">
                      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center shrink-0">
                        v{h.version || 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-slate-800">{h.action}</span>
                          <span className="text-slate-400">{new Date(h.timestamp).toLocaleString()}</span>
                        </div>
                        <div className="text-slate-600 mt-0.5">By: <span className="font-medium">{h.performed_by}</span> ({h.role})</div>
                        <div className="text-slate-500 mt-1 italic">{h.notes}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 text-right">
                <button
                  onClick={() => setHistoryModalPolicy(null)}
                  className="px-4 py-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-medium"
                >
                  Close History
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default LocalConveyancePolicyMaster;
